/**
 * Skins: "dark" (default), "light" or "system" (follow the device's light/dark
 * setting). The skin is shown by setting <html data-theme="light|dark"> - the
 * colors are in shared/styles/variables.scss. Saved on the device (so it is
 * right before login) and on the account (so all devices use it).
 */
import { fetcher } from "../utils/fetcher";
import useAuthStore from "../store/authStore";

export const THEMES = ["dark", "light", "system"];
const THEME_STORAGE_KEY = "somigo.theme";
const DEFAULT_THEME = "dark";

const systemDarkQuery = window.matchMedia?.("(prefers-color-scheme: dark)");
let currentTheme = DEFAULT_THEME;
const listeners = new Set();

const readFromDevice = () => {
  try {
    const theme = localStorage.getItem(THEME_STORAGE_KEY);
    return THEMES.includes(theme) ? theme : DEFAULT_THEME;
  } catch {
    return DEFAULT_THEME;
  }
};

const storeOnDevice = (theme) => {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // Storage unavailable (e.g. private mode) - the skin still applies
  }
};

// Show the skin on the page ("system" follows the device)
const showOnPage = () => {
  const isLight =
    currentTheme === "light" ||
    (currentTheme === "system" && systemDarkQuery && !systemDarkQuery.matches);
  document.documentElement.dataset.theme = isLight ? "light" : "dark";
};

const switchTheme = (theme) => {
  if (!THEMES.includes(theme)) return;
  currentTheme = theme;
  storeOnDevice(theme);
  showOnPage();
  listeners.forEach((listener) => listener(theme));
};

export const getTheme = () => currentTheme;

// For the skin picker: called with the new skin when it changes
export const subscribeToTheme = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

// Called once at startup, before the first render
export const initTheme = () => {
  currentTheme = readFromDevice();
  showOnPage();
  systemDarkQuery?.addEventListener?.("change", showOnPage);
};

// The user picked a skin in settings
export const setTheme = async (theme) => {
  switchTheme(theme);
  const { user } = useAuthStore.getState();
  if (!user) return;
  useAuthStore.setState({ user: { ...user, theme } });
  try {
    await fetcher.put("/api/auth/theme", { theme });
  } catch (err) {
    console.error("Error saving skin:", err);
  }
};

// Use a skin saved on the account (also when it was changed on another
// device - the server sends "user:theme")
export const applyTheme = (theme) => {
  const { user } = useAuthStore.getState();
  if (user && user.theme !== theme) {
    useAuthStore.setState({ user: { ...user, theme } });
  }
  if (theme !== currentTheme) switchTheme(theme);
};

// The account's skin wins (it may have been changed on another device).
// Called on start/login and when the app comes back to the foreground.
export const refreshAccountTheme = async () => {
  if (!useAuthStore.getState().user) return;
  try {
    const { theme } = await fetcher.get("/api/auth/theme");
    if (theme) applyTheme(theme);
  } catch (err) {
    console.error("Error loading skin:", err);
  }
};
